import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";

// Lazy load route components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AccountSetup = lazy(() => import("./pages/AccountSetup"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ClientOnboarding = lazy(() => import("./pages/client/ClientOnboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const TeamSetup = lazy(() => import("./pages/TeamSetup"));
const OperationalDragScore = lazy(() => import("./pages/OperationalDragScore"));
const BookCall = lazy(() => import("./pages/BookCall"));
const Scheduler = lazy(() => import("./pages/Scheduler"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));

// Client Portal
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const EngagementDetail = lazy(() => import("./pages/client/EngagementDetail"));

const ClientBilling = lazy(() => import("./pages/client/ClientBilling"));

const ClientProgress = lazy(() => import("./pages/client/ClientProgress"));
const ClientResources = lazy(() => import("./pages/client/ClientResources"));

// Admin Portal
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));

const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));




const AdminSubmissions = lazy(() => import("./pages/admin/AdminSubmissions"));


const AdminProjects = lazy(() => import("./pages/admin/AdminProjects"));
const ProjectDetail = lazy(() => import("./pages/admin/ProjectDetail"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminBottleneckMap = lazy(() => import("./pages/admin/AdminBottleneckMap"));
const AdminAutomations = lazy(() => import("./pages/admin/AdminAutomations"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminQuoteEngine = lazy(() => import("./pages/admin/AdminQuoteEngine"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel"));

const ExternalRedirect = ({ url }: { url: string }) => {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry auth/permission errors
        const code = error?.code || error?.status;
        if (code === 'PGRST301' || code === 401 || code === 403 || code === 404) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <ErrorBoundary>
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div></div>}>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/account-setup" element={<AccountSetup />} />
              <Route path="/portal/invite" element={<Navigate to="/account-setup" replace />} />
              <Route path="/client/login" element={<Navigate to="/login" replace />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/operational-drag-score" element={<OperationalDragScore />} />
              <Route path="/operational-drag-score/197" element={<ExternalRedirect url="https://operational-drag-score-197.lovable.app" />} />
              <Route path="/book-call" element={<BookCall />} />
              <Route path="/schedule/:kind" element={<Scheduler />} />
              <Route path="/booking-confirmed" element={<Navigate to="/book-call" replace />} />
              <Route path="/score" element={<Navigate to="/operational-drag-score" replace />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-canceled" element={<PaymentCanceled />} />
              <Route path="/team-setup" element={<TeamSetup />} />
              {/* Redirects for removed suite pages */}
              <Route path="/flourish-suite" element={<Navigate to="/" replace />} />
              <Route path="/foundation-suite" element={<Navigate to="/" replace />} />
              <Route path="/framework-suite" element={<Navigate to="/" replace />} />
              <Route path="/onboarding" element={<ProtectedRoute><ClientOnboarding /></ProtectedRoute>} />
              
              {/* Client Portal Routes */}
              <Route path="/client/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
              <Route path="/client/progress" element={<ProtectedRoute><ClientProgress /></ProtectedRoute>} />
              <Route path="/client/engagement/:id" element={<ProtectedRoute><EngagementDetail /></ProtectedRoute>} />
              
              <Route path="/client/billing" element={<ProtectedRoute><ClientBilling /></ProtectedRoute>} />
              <Route path="/client/resources" element={<ProtectedRoute><ClientResources /></ProtectedRoute>} />
              
              
              {/* Admin Portal Routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/marketing" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/deals" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/submissions" element={<ProtectedRoute requireAdmin><AdminSubmissions /></ProtectedRoute>} />
              <Route path="/admin/clients" element={<ProtectedRoute requireAdmin><AdminClients /></ProtectedRoute>} />
              <Route path="/admin/engagements" element={<Navigate to="/admin/clients" replace />} />
              
              <Route path="/admin/invoices" element={<ProtectedRoute requireAdmin><AdminInvoices /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/security" element={<Navigate to="/admin/settings" replace />} />
              
              
              
              <Route path="/admin/projects" element={<ProtectedRoute requireAdmin><AdminProjects /></ProtectedRoute>} />
              <Route path="/admin/projects/:id" element={<ProtectedRoute requireAdmin><ProjectDetail /></ProtectedRoute>} />
              <Route path="/admin/calendar" element={<ProtectedRoute requireAdmin><AdminCalendar /></ProtectedRoute>} />
              <Route path="/admin/scheduling" element={<Navigate to="/admin/calendar" replace />} />
              <Route path="/admin/bottleneck-map" element={<ProtectedRoute requireAdmin><AdminBottleneckMap /></ProtectedRoute>} />
              <Route path="/admin/automations" element={<ProtectedRoute requireAdmin><AdminAutomations /></ProtectedRoute>} />
              <Route path="/admin/team" element={<ProtectedRoute requireAdmin><AdminTeam /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute requireAdmin><AdminReports /></ProtectedRoute>} />
              <Route path="/admin/leads" element={<ProtectedRoute requireAdmin><AdminLeads /></ProtectedRoute>} />
              <Route path="/admin/quote-engine" element={<ProtectedRoute requireAdmin><AdminQuoteEngine /></ProtectedRoute>} />
              <Route path="/admin/notifications" element={<ProtectedRoute requireAdmin><AdminNotifications /></ProtectedRoute>} />
              <Route path="/admin/funnel" element={<ProtectedRoute requireAdmin><AdminFunnel /></ProtectedRoute>} />
              <Route path="/admin/resources" element={<Navigate to="/admin/settings?tab=guide" replace />} />
               
               
               {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
